// src/ui/GroupCallDock.jsx — the group call surface.
//
// 🟢 THREE STATES, ONE COMPONENT: the incoming ring, the call itself, and
//    nothing. Everything it draws comes from services/groupCalls.js; it holds no
//    call state of its own, because two places deciding whether a call is up is
//    how a dock outlives its call.
//
// 🟢 FIVE LAYOUTS, BECAUSE ONE GRID IS NOT A CALL SCREEN. A 2×2 of equal tiles
//    is right for four people talking and wrong for a screen-share, wrong for a
//    presenter, and wrong when the reader wants the call small and out of the
//    way while they work. Each layout below answers a different question:
//      grid       — who is here?            (equal tiles)
//      speaker    — who is talking?         (active talker large, rest as a strip)
//      sidebar    — who is here, while I read the chat? (stage + vertical rail)
//      spotlight  — one person, nothing else
//      minimised  — the call keeps running while I do something else
//    The choice is remembered, because a layout re-picked on every call is not a
//    preference.
//
// 🟢 IT MOVES AND IT RESIZES, and it floats above everything else in the app —
//    Vibes, the calls list, a profile, any modal. A call is the thing you are
//    doing; whatever is underneath it is the thing you are doing it about. The
//    frame is clamped to the viewport on every restore and on every window
//    resize, so a position saved on a large monitor can never strand the dock
//    off-screen on a laptop.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Avatar from './Avatar';
import { Phone, Video, X, Mic, Send } from './icons';
import { MARGIN, SNAP, vw, vh, readJSON, writeJSON, clampPos } from './floating';
import {
  subscribeGroupCalls, getGroupCallState, joinGroupCall, declineGroupCall,
  leaveGroupCall, toggleGroupMute, toggleGroupCamera, sendCallMessage,
  reinvite, invitesLeft, syncActiveCall, setPinnedPeer,
} from '../services/groupCalls';

/* ══════════════════════════════════════════════════════════════════════════
   🔊 WHO IS TALKING

   Speaker and spotlight views are only worth having if the app can answer that
   question — otherwise "active speaker" means "whoever the object keys happened
   to be ordered by", which is worse than a plain grid because it looks decided.

   🟥 THE ANALYSER GRAPH IS BUILT ONCE PER SET OF STREAMS, not once per render.
      The call state emits on every mute toggle, every ICE update and every
      chat message, so an effect that depended on the peers OBJECT would tear
      down and rebuild an AudioContext several times a second. The signature
      below changes only when somebody joins, leaves, or their stream attaches.

   🟢 HYSTERESIS, so the stage does not strobe. A new person has to be loudest
      for two consecutive samples to take the stage, and silence changes
      nothing at all — when everyone stops talking the last speaker stays put,
      which is what a viewer expects and what every conferencing app does.
   ══════════════════════════════════════════════════════════════════════════ */
const SPEAK_FLOOR = 12;      // below this it is room noise, not speech
const SAMPLE_MS = 350;

function useActiveSpeaker(peers) {
  const [active, setActive] = useState(null);
  const peersRef = useRef(peers);
  peersRef.current = peers;
  const activeRef = useRef(null);

  const sig = useMemo(
    () => Object.entries(peers || {})
      .map(([u, p]) => u + (p && p.stream ? '+' : '-'))
      .sort().join(','),
    [peers],
  );

  useEffect(() => {
    const AC = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
    if (!AC) return undefined;
    let ctx;
    try { ctx = new AC(); } catch { return undefined; }

    const nodes = [];
    for (const [uid, p] of Object.entries(peersRef.current || {})) {
      const stream = p && p.stream;
      if (!stream || typeof stream.getAudioTracks !== 'function') continue;
      if (stream.getAudioTracks().length === 0) continue;
      try {
        const src = ctx.createMediaStreamSource(stream);
        const an = ctx.createAnalyser();
        an.fftSize = 512;
        an.smoothingTimeConstant = 0.6;
        src.connect(an);
        nodes.push({ uid, an, buf: new Uint8Array(an.frequencyBinCount) });
      } catch { /* a stream whose audio track is already gone */ }
    }
    if (!nodes.length) { try { ctx.close(); } catch { /* already closed */ } return undefined; }

    let streakUid = null;
    let streakN = 0;
    const tick = () => {
      let bestUid = null;
      let best = 0;
      for (const n of nodes) {
        n.an.getByteFrequencyData(n.buf);
        let sum = 0;
        for (let i = 0; i < n.buf.length; i += 1) sum += n.buf[i] * n.buf[i];
        const rms = Math.sqrt(sum / n.buf.length);
        if (rms > best) { best = rms; bestUid = n.uid; }
      }
      if (best < SPEAK_FLOOR) return;                 // silence keeps the stage
      if (bestUid === activeRef.current) { streakUid = bestUid; streakN = 0; return; }
      if (streakUid === bestUid) streakN += 1; else { streakUid = bestUid; streakN = 1; }
      if (streakN >= 2) { activeRef.current = bestUid; setActive(bestUid); }
    };
    const id = setInterval(tick, SAMPLE_MS);
    return () => { clearInterval(id); try { ctx.close(); } catch { /* already closed */ } };
  }, [sig]);

  return active;
}

/* ══════════════════════════════════════════════════════════════════════════
   🪟 THE FRAME — where the dock sits and how big it is
   ══════════════════════════════════════════════════════════════════════════ */
const FRAME_KEY = 'allo_gcd_frame';
const LAYOUT_KEY = 'allo_gcd_layout';
/* 🟢 MARGIN / SNAP / clampPos / readJSON now live in ./floating, shared with the
   1-on-1 dock — one set of off-by-ones instead of two that drift apart. */
const MIN_W = 320;
const MIN_H = 260;
const PILL_W = 258;
const PILL_H = 62;
const DEFAULT = { x: null, y: null, w: 460, h: 430 };


function clampSize(w, h) {
  return {
    w: Math.max(MIN_W, Math.min(w, Math.max(MIN_W, vw() - MARGIN * 2))),
    h: Math.max(MIN_H, Math.min(h, Math.max(MIN_H, vh() - MARGIN * 2))),
  };
}

/* ══════════════════════════════════════════════════════════════════════════
   🎛 THE LAYOUT SWITCHER — drawn, not labelled

   Each glyph is a miniature of the layout it selects, which is readable at a
   glance and needs no translation. Words ("Speaker", "Sidebar") take four times
   the room and still have to be learned.
   ══════════════════════════════════════════════════════════════════════════ */
const LAYOUTS = [
  { id: 'grid', title: 'Grid — everyone the same size' },
  { id: 'speaker', title: 'Speaker — whoever is talking, large' },
  { id: 'sidebar', title: 'Sidebar — a stage with a people rail' },
  { id: 'spotlight', title: 'Spotlight — one person, nothing else' },
];

function LayoutGlyph({ id }) {
  const r = (x, y, w, h, k) => <rect key={k} x={x} y={y} width={w} height={h} rx="1.6" />;
  const shapes = {
    grid: [r(1, 1, 7, 6, 'a'), r(10, 1, 7, 6, 'b'), r(1, 9, 7, 6, 'c'), r(10, 9, 7, 6, 'd')],
    speaker: [r(1, 1, 16, 9, 'a'), r(1, 11.5, 4.6, 3.5, 'b'), r(6.7, 11.5, 4.6, 3.5, 'c'), r(12.4, 11.5, 4.6, 3.5, 'd')],
    sidebar: [r(1, 1, 11.5, 14, 'a'), r(13.8, 1, 3.2, 4.3, 'b'), r(13.8, 5.8, 3.2, 4.3, 'c'), r(13.8, 10.6, 3.2, 4.4, 'd')],
    spotlight: [r(1, 1, 16, 14, 'a')],
  };
  return (
    <svg viewBox="0 0 18 16" width="16" height="15" fill="currentColor" aria-hidden="true">
      {shapes[id] || shapes.grid}
    </svg>
  );
}

/** A <video> that follows a MediaStream prop — srcObject cannot be set in JSX. */
function Stream({ stream, muted, className }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    /* 🟥 ONLY WHEN IT ACTUALLY CHANGED. Re-assigning the same MediaStream
       restarts playback, which shows as a black flash on every render — and
       this component re-renders whenever any OTHER tile changes. */
    if (el.srcObject !== (stream || null)) el.srcObject = stream || null;
  }, [stream]);
  return <video ref={ref} className={className} autoPlay playsInline muted={!!muted} />;
}

function Tile({ peer, label, self, cameraOff, muted, speaking, pinned, onPin, size }) {
  const live = !!(peer && peer.stream && peer.stream.getVideoTracks
    && peer.stream.getVideoTracks().some((t) => t.enabled && t.readyState === 'live'));
  return (
    <div
      className={`gcd-tile ${self ? 'self' : ''} ${speaking ? 'speaking' : ''} ${pinned ? 'pinned' : ''} ${size || ''}`}
      onClick={onPin}
      role={onPin ? 'button' : undefined}
      tabIndex={onPin ? 0 : undefined}
      onKeyDown={onPin ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPin(); } } : undefined}
      title={onPin ? (pinned ? 'Unpin' : 'Pin to the stage') : undefined}
    >
      {live && !cameraOff
        ? <Stream stream={peer.stream} muted={self} className="gcd-video" />
        : (
          <div className="gcd-ph">
            <Avatar profile={{ fullName: label, profileImage: peer && peer.image }} size="lg" />
          </div>
        )}
      <span className="gcd-name">
        {/* 🟢 THE SAME MUTE MARK THE PHONE PUTS ON A TILE — a crossed mic on the
            person, not a line of text somewhere else. It is the answer to "why
            can I not hear them", and it has to be where they are. */}
        {muted && <Mic width={11} height={11} className="gcd-muted" />}
        {label}
      </span>
      {pinned && <span className="gcd-pin" aria-hidden="true">PINNED</span>}
    </div>
  );
}

function elapsed(since) {
  if (!since) return '';
  const s = Math.max(0, Math.floor((Date.now() - since) / 1000));
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const mm = h ? String(m % 60).padStart(2, '0') : String(m);
  return `${h ? `${h}:` : ''}${mm}:${String(s % 60).padStart(2, '0')}`;
}

const STATE_LABEL = {
  calling: 'calling…', ringing: 'ringing…', joined: 'in the call',
  declined: 'declined', timed_out: 'no answer', expired: 'no answer',
};

export default function GroupCallDock({ myUid, myProfile, chats = [] }) {
  const [st, setSt] = useState(getGroupCallState());
  useEffect(() => subscribeGroupCalls(setSt), []);

  /* 🟥 THE DISMISSAL LIVES HERE because this is where the chat list is. The
     engine cannot watch `activeCall` on its own — it has no listener of its own
     on the chats collection, and adding a second one would be a second opinion
     about the same documents. */
  useEffect(() => { syncActiveCall(chats); }, [chats]);

  const [tab, setTab] = useState('');        // '' | 'chat' | 'people'
  const [draft, setDraft] = useState('');
  const [pinned, setPinned] = useState(null);
  const chatEnd = useRef(null);

  const [layout, setLayout] = useState(() => {
    try {
      const v = localStorage.getItem(LAYOUT_KEY);
      return ['grid', 'speaker', 'sidebar', 'spotlight', 'minimised'].includes(v) ? v : 'grid';
    } catch { return 'grid'; }
  });
  const pickLayout = useCallback((id) => {
    setLayout(id);
    try { localStorage.setItem(LAYOUT_KEY, id); } catch { /* private mode */ }
  }, []);

  const minimised = layout === 'minimised';
  const [frame, setFrame] = useState(() => {
    const s = readJSON(FRAME_KEY, DEFAULT);
    const size = clampSize(s.w || DEFAULT.w, s.h || DEFAULT.h);
    return { ...size, ...clampPos(s.x, s.y, size.w, size.h) };
  });
  /* 🟥 A MIRROR, NOT A READ-THROUGH-SETTER. Grabbing the current frame with
     `setFrame(f => { base = f; return f; })` schedules a render to read a value
     and runs twice under StrictMode; a ref is the honest way to read state
     inside an event handler that then keeps handling events. */
  const frameRef = useRef(frame);
  frameRef.current = frame;
  const [busy, setBusy] = useState('');      // '' | 'drag' | 'resize'

  const boxW = minimised ? PILL_W : frame.w;
  const boxH = minimised ? PILL_H : frame.h;
  const at = clampPos(frame.x, frame.y, boxW, boxH);

  /* a window that got smaller must not leave the dock outside it */
  useEffect(() => {
    const on = () => setFrame((f) => {
      const size = clampSize(f.w, f.h);
      const w = minimised ? PILL_W : size.w;
      const h = minimised ? PILL_H : size.h;
      return { ...f, ...size, ...clampPos(f.x, f.y, w, h) };
    });
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [minimised]);

  /* ── drag by the header ────────────────────────────────────────────────── */
  const onDragStart = useCallback((e) => {
    /* a control inside the header is a control, not a handle */
    if (e.target.closest && e.target.closest('button')) return;
    e.preventDefault();
    setBusy('drag');
    const startX = e.clientX;
    const startY = e.clientY;
    const base = frameRef.current;
    const move = (ev) => {
      setFrame((f) => ({
        ...f,
        ...clampPos(base.x + (ev.clientX - startX), base.y + (ev.clientY - startY), boxW, boxH),
      }));
    };
    const up = () => {
      setBusy('');
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setFrame((f) => {
        /* 🧲 SNAP — released near an edge, it sits flush against it. Parking a
           call window by eye is fiddly and the result is always a few pixels
           off; this makes "put it in the corner" a gesture rather than a task. */
        let { x, y } = f;
        if (x <= MARGIN + SNAP) x = MARGIN;
        else if (x + boxW >= vw() - MARGIN - SNAP) x = vw() - boxW - MARGIN;
        if (y <= MARGIN + SNAP) y = MARGIN;
        else if (y + boxH >= vh() - MARGIN - SNAP) y = vh() - boxH - MARGIN;
        const next = { ...f, ...clampPos(x, y, boxW, boxH) };
        writeJSON(FRAME_KEY, next);
        return next;
      });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, [boxW, boxH]);

  /* ── resize from the bottom-right corner ───────────────────────────────── */
  const onResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setBusy('resize');
    const startX = e.clientX;
    const startY = e.clientY;
    const base = frameRef.current;
    const move = (ev) => {
      const size = clampSize(base.w + (ev.clientX - startX), base.h + (ev.clientY - startY));
      setFrame((f) => ({ ...f, ...size, ...clampPos(f.x, f.y, size.w, size.h) }));
    };
    const up = () => {
      setBusy('');
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setFrame((f) => { writeJSON(FRAME_KEY, f); return f; });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, []);

  /* the timer is the only thing here that ticks on its own */
  const [, force] = useState(0);
  useEffect(() => {
    if (st.status !== 'active') return undefined;
    const t = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [st.status]);

  useEffect(() => {
    if (tab === 'chat') chatEnd.current?.scrollIntoView({ block: 'end' });
  }, [tab, st.chat.length]);

  const chat = useMemo(
    () => (chats || []).find((c) => c.id === st.groupId) || null,
    [chats, st.groupId],
  );

  const speaking = useActiveSpeaker(st.peers);

  const title = st.groupName || chat?.name || 'Group';
  const nameOf = useCallback(
    (uid) => (uid === myUid ? 'You' : (st.peers[uid]?.name || chat?.participantNames?.[uid] || 'Allo user')),
    [myUid, st.peers, chat],
  );

  /* every face in the call, self first — the layouts below all slice this */
  const tiles = useMemo(() => {
    const self = {
      uid: myUid,
      peer: { stream: st.localStream, image: myProfile?.profileImage },
      label: 'You',
      self: true,
      muted: st.muted,
      cameraOff: st.cameraOff,
    };
    const rest = Object.keys(st.peers).map((uid) => ({
      uid,
      peer: st.peers[uid],
      label: nameOf(uid),
      self: false,
      muted: st.peers[uid]?.muted,
      cameraOff: false,
    }));
    return [self, ...rest];
  }, [myUid, myProfile, st.localStream, st.muted, st.cameraOff, st.peers, nameOf]);

  /* who holds the stage in speaker / sidebar / spotlight */
  const starUid = useMemo(() => {
    if (pinned && tiles.some((t) => t.uid === pinned)) return pinned;
    if (speaking && tiles.some((t) => t.uid === speaking)) return speaking;
    const firstRemote = tiles.find((t) => !t.self);
    return (firstRemote || tiles[0]).uid;
  }, [pinned, speaking, tiles]);

  /* ═══ 📶 THE BUDGET FOLLOWS THE EYE ═══════════════════════════════════════
     🟢 A MESH CAN SEND A DIFFERENT ENCODING TO EACH PERSON, which an SFU cannot
        without simulcast layers. So the person on the stage is worth more of the
        upload than the thumbnails nobody is looking at — and "on the stage" is
        already computed above: a deliberate pin, or whoever is speaking.

     🟥 SETTLED FIRST, THOUGH. Every change of stage re-encodes two legs and each
        re-encode costs a keyframe, so following the active speaker instantly
        would spend more on keyframes than the extra bitrate is worth in a lively
        conversation. Two seconds of holding the stage earns the bandwidth.

     🟢 AND A STAGE HELD BY YOURSELF BUDGETS NOBODY: there is no leg to yourself,
        and demoting everyone else to a thumbnail because you are talking would
        be exactly backwards. */
  useEffect(() => {
    const who = starUid && starUid !== myUid ? starUid : null;
    const t = setTimeout(() => { try { setPinnedPeer(who); } catch { /* call already gone */ } }, 2000);
    return () => clearTimeout(t);
  }, [starUid, myUid]);

  if (st.status === 'idle') return null;

  const togglePin = (uid) => setPinned((p) => (p === uid ? null : uid));

  /* ── the ring ──────────────────────────────────────────────────────────── */
  if (st.status === 'ringing') {
    return (
      <div className="gcd gcd-ring">
        <Avatar chat={chat} size="lg" myUid={myUid} />
        <div className="gcd-ring-who">
          <b>{title}</b>
          <i>
            {st.callerName ? `${st.callerName} · ` : ''}
            Incoming {st.callType === 'video' ? 'video' : 'voice'} call
          </i>
        </div>
        <div className="gcd-ring-btns">
          <button className="gcd-btn decline" onClick={declineGroupCall} aria-label="Decline">
            <X width={19} height={19} />
          </button>
          <button className="gcd-btn accept" onClick={() => joinGroupCall(chat)} aria-label="Join">
            {st.callType === 'video' ? <Video width={19} height={19} /> : <Phone width={19} height={19} />}
          </button>
        </div>
      </div>
    );
  }

  const joined = Object.keys(st.peers);
  const timeLabel = st.status === 'joining' ? (st.note || 'Joining…') : elapsed(st.startedAt);

  /* ── minimised: the call keeps running, out of the way ─────────────────── */
  if (minimised) {
    return (
      <div
        className={`gcd gcd-pill ${busy ? `is-${busy}` : ''}`}
        style={{ left: at.x, top: at.y, width: PILL_W, height: PILL_H }}
        onPointerDown={onDragStart}
      >
        <Avatar chat={chat} size="sm" myUid={myUid} />
        <span className="gcd-pill-who">
          <b>{title}</b>
          <i>{timeLabel} · {joined.length + 1}</i>
        </span>
        <button
          className={`gcd-mini-btn ${st.muted ? 'on' : ''}`}
          onClick={toggleGroupMute}
          title={st.muted ? 'Unmute' : 'Mute'}
          aria-label={st.muted ? 'Unmute' : 'Mute'}
        >
          <Mic width={14} height={14} />
        </button>
        <button
          className="gcd-mini-btn"
          onClick={() => pickLayout('grid')}
          title="Back to the call window"
          aria-label="Expand the call"
        >
          <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true">
            <path d="M2 9h2v3h3v2H2V9zm12-2h-2V4H9V2h5v5z" />
          </svg>
        </button>
        <button className="gcd-mini-btn end" onClick={leaveGroupCall} title="Leave" aria-label="Leave the call">
          <X width={14} height={14} />
        </button>
      </div>
    );
  }

  /* 🟢 EVERY INVITEE, NOT ONLY THE ONES INSIDE. A grid of the people who
     answered makes a call somebody has not picked up yet look like a call
     nobody picked up — the phone draws these states for the same reason, and
     the server broadcasts them for both of us. */
  const others = Object.entries(st.invitees).filter(([uid]) => uid !== myUid);
  const waiting = others.filter(([uid, i]) => !st.peers[uid] && (i?.state === 'calling' || i?.state === 'ringing'));
  const absent = others.filter(([, i]) => ['declined', 'timed_out', 'expired'].includes(i?.state));

  const star = tiles.find((t) => t.uid === starUid) || tiles[0];
  const rest = tiles.filter((t) => t.uid !== star.uid);

  const tileOf = (t, size) => (
    <Tile
      key={t.uid}
      peer={t.peer}
      label={t.label}
      self={t.self}
      muted={t.muted}
      cameraOff={t.cameraOff}
      speaking={speaking === t.uid && !t.self}
      pinned={pinned === t.uid}
      onPin={() => togglePin(t.uid)}
      size={size}
    />
  );

  let stage = null;
  if (layout === 'grid') {
    stage = (
      <div className={`gcd-grid n${Math.min(9, tiles.length)}`}>
        {tiles.map((t) => tileOf(t))}
      </div>
    );
  } else if (layout === 'spotlight') {
    stage = <div className="gcd-solo">{tileOf(star, 'big')}</div>;
  } else if (layout === 'sidebar') {
    stage = (
      <div className="gcd-split">
        <div className="gcd-stage">{tileOf(star, 'big')}</div>
        {rest.length > 0 && <div className="gcd-rail">{rest.map((t) => tileOf(t, 'mini'))}</div>}
      </div>
    );
  } else {
    stage = (
      <div className="gcd-speaker">
        <div className="gcd-stage">{tileOf(star, 'big')}</div>
        {rest.length > 0 && <div className="gcd-strip">{rest.map((t) => tileOf(t, 'mini'))}</div>}
      </div>
    );
  }

  return (
    <div
      className={`gcd gcd-live ${busy ? `is-${busy}` : ''}`}
      style={{ left: at.x, top: at.y, width: frame.w, height: frame.h }}
      data-layout={layout}
    >
      <div className="gcd-head" onPointerDown={onDragStart}>
        <b>{title}</b>
        <i>{timeLabel}</i>
        {/* 🟢 ALONE IS A STATE, NOT A FAULT. Now that one person leaving no longer
            ends the call, a room can legitimately hold just you — and without
            saying so, "1 in call" beside your own tile looks exactly like the bug
            that used to close the dock. The room is open; people can come back. */}
        <span className="gcd-count">
          {joined.length === 0 ? 'Waiting for others' : `${joined.length + 1} in call`}
        </span>
        <span className="gcd-seg" role="group" aria-label="Call layout">
          {LAYOUTS.map((l) => (
            <button
              key={l.id}
              className={`gcd-seg-btn ${layout === l.id ? 'on' : ''}`}
              onClick={() => pickLayout(l.id)}
              title={l.title}
              aria-label={l.title}
              aria-pressed={layout === l.id}
            >
              <LayoutGlyph id={l.id} />
            </button>
          ))}
        </span>
        <button
          className="gcd-seg-btn solo"
          onClick={() => pickLayout('minimised')}
          title="Minimise — the call keeps running"
          aria-label="Minimise the call"
        >
          <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true">
            <rect x="3" y="7.2" width="10" height="1.9" rx=".9" />
          </svg>
        </button>
      </div>

      <div className="gcd-body">{stage}</div>

      {waiting.length > 0 && (
        <div className="gcd-waiting">
          {waiting.map(([uid, i]) => (
            <span key={uid} className="gcd-chip">{nameOf(uid)} · {STATE_LABEL[i.state]}</span>
          ))}
        </div>
      )}

      {st.error && <div className="gcd-err">{st.error}</div>}

      {tab === 'chat' && (
        <div className="gcd-panel">
          <div className="gcd-chat">
            {st.chat.length === 0
              ? <div className="gcd-empty">No messages during this call yet</div>
              : st.chat.map((m) => (
                <div key={m.id} className={`gcd-msg ${m.senderId === myUid ? 'mine' : ''}`}>
                  {m.senderId !== myUid && <b>{m.senderName || nameOf(m.senderId)}</b>}
                  <span>{m.text}</span>
                </div>
              ))}
            <div ref={chatEnd} />
          </div>
          <form
            className="gcd-compose"
            onSubmit={(e) => { e.preventDefault(); sendCallMessage(draft); setDraft(''); }}
          >
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Message everyone in this call"
              aria-label="Message everyone in this call"
            />
            <button type="submit" className="gcd-send" disabled={!draft.trim()} aria-label="Send">
              <Send width={15} height={15} />
            </button>
          </form>
        </div>
      )}

      {tab === 'people' && (
        <div className="gcd-panel">
          <div className="gcd-people">
            {others.length === 0 && <div className="gcd-empty">Nobody else was invited</div>}
            {others.map(([uid, i]) => {
              const left = invitesLeft(uid);
              const canRing = ['declined', 'timed_out'].includes(i?.state) && left > 0;
              return (
                <div key={uid} className="gcd-person">
                  <Avatar profile={{ fullName: nameOf(uid), profileImage: st.peers[uid]?.image }} size="sm" />
                  <span>
                    <b>{nameOf(uid)}</b>
                    <i>{STATE_LABEL[i?.state] || ''}</i>
                  </span>
                  {/* 🟥 THE REMAINING COUNT IS THE SERVER'S, so it is already the
                      same number the phone shows. Ringing somebody twice here
                      and once there really does use all three, because both
                      clients are reading and incrementing one tally rather than
                      keeping their own. */}
                  {canRing && (
                    <button className="gcd-ring-again" onClick={() => reinvite(uid)}>
                      Ring again · {left} left
                    </button>
                  )}
                  {!canRing && i?.state === 'expired' && <span className="gcd-spent">no invites left</span>}
                </div>
              );
            })}
            {absent.length > 0 && (
              <div className="gcd-note">
                Somebody who declined can be rung again, up to {st.maxInvites} times in total —
                counted across your phone and this browser.
              </div>
            )}
          </div>
        </div>
      )}

      <div className="gcd-ctrls">
        <button
          className={`gcd-btn ${st.muted ? 'on' : ''}`}
          onClick={toggleGroupMute}
          title={st.muted ? 'Unmute' : 'Mute'}
        >{st.muted ? 'Unmute' : 'Mute'}</button>
        {st.callType === 'video' && (
          <button
            className={`gcd-btn ${st.cameraOff ? 'on' : ''}`}
            onClick={toggleGroupCamera}
            title={st.cameraOff ? 'Turn the camera on' : 'Turn the camera off'}
          >{st.cameraOff ? 'Camera on' : 'Camera off'}</button>
        )}
        <button
          className={`gcd-btn ${tab === 'chat' ? 'on' : ''}`}
          onClick={() => setTab(tab === 'chat' ? '' : 'chat')}
          title="Chat with everyone in this call"
        >Chat{st.chat.length ? ` · ${st.chat.length}` : ''}</button>
        <button
          className={`gcd-btn ${tab === 'people' ? 'on' : ''}`}
          onClick={() => setTab(tab === 'people' ? '' : 'people')}
          title="Who is in this call"
        >People</button>
        <button className="gcd-btn end" onClick={leaveGroupCall}>Leave</button>
      </div>

      {/* the corner you pull to make it bigger */}
      <span
        className="gcd-resize"
        onPointerDown={onResizeStart}
        role="separator"
        aria-label="Resize the call window"
        title="Drag to resize"
      />
    </div>
  );
}
